import { MouseEventHandler, useMemo } from "react";
import { Text } from "@imminently/react/ui/core/text";
import { NodeApi, NodeRendererProps, Tree } from "react-arborist";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { useComplaintCodes, NodeData } from "./data";
import { useComplaintCodeContext } from "./ComplaintCodeContext";
import { cn } from "@/lib/utils";
import { matchComplaintNode } from "./util";

// Sort tree data alphabetically (recursive)
const sortTreeData = (data: NodeData[]): NodeData[] => {
  return data
    .map(node => ({
      ...node,
      children: node.children ? sortTreeData(node.children) : undefined
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

// need to build an array by iterating up the tree to get the full path
const getPath = (node: NodeApi): NodeApi[] => {
  const path: NodeApi[] = [];
  let current: NodeApi | null = node;
  while (current) {
    path.unshift(current);
    current = current.parent;
  }
  return path;
};

const searchMatch = (n: NodeApi, searchTerm: string) => {
  if (!searchTerm) return true;
  // if (!n.isLeaf) return false;
  const path = getPath(n).map(n => n.data.name).filter(Boolean).join("/");
  return matchComplaintNode(n.data.name, path, searchTerm);
  // const words = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
  // // match if all words are found in either the name or the path
  // return words.every(word => 
  //   n.data.name.toLowerCase().includes(word) || 
  //   path.some(p => p.includes(word))
  // );
};

const ComplaintRow = (props: NodeRendererProps<NodeData>) => {
  const { node } = props;
  const { selected } = useComplaintCodeContext();

  const onClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (node.isLeaf) {
      return node.select();
    }
    // For branch nodes: toggle expand/collapse and select
    node.toggle();
    node.select();
  };

  // Get child count for folders
  const childCount = node.children ? node.children.length : 0;

  // Determine background color based on node type
  const isFolder = !node.isLeaf;
  const isSelected = selected?.id === node.data.id;

  const marginLeft = node.level * 16;

  return (
    <div
      data-active={isSelected}
      onClick={onClick}
      style={{ marginLeft }}
      className={cn(
        'flex items-center justify-between py-2 px-4 gap-1 rounded-lg cursor-pointer hover:opacity-80 border border-transparent',
        {
          'bg-royalblue-50': isFolder && !isSelected,
          'bg-yellow-50': isSelected,
          'border-royalblue-50': !isFolder && !isSelected,
          'border-yellow-400': isSelected,
        }
      )}
    >
      <div className="flex items-center gap-1 overflow-hidden">
        {
          node.children ? (
            <>
              {
                node.isOpen ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />
              }
              {/* {
                node.isOpen ? <FolderOpenIcon size={16} /> : <FolderIcon size={16} />
              } */}
            </>
          ) : null
        }
        <Text noWrap>{node.data.name}</Text>
      </div>
      {isFolder && childCount > 0 && (
        <div className="flex items-center justify-center w-6 h-6 bg-white text-xs font-medium rounded-full">
          {childCount}
        </div>
      )}
    </div>
  );
};

export const ComplaintCodeTree = () => {
  const { filter, setSelected } = useComplaintCodeContext();
  const complaintData = useComplaintCodes();

  // Memoize the sorted data to avoid re-sorting on every render
  const sortedData = useMemo(() => sortTreeData(complaintData), [complaintData]);

  const handleSelect = (nodes: NodeApi<NodeData>[]) => {
    // we have disabled multi select, so only one node will be selected
    const node = nodes[0];
    if (node) {
      setSelected(node.data);
    }
  };

  return (
    <div className="flex-1">
      <Tree
        disableDrag
        disableDrop
        disableMultiSelection
        disableEdit
        rowHeight={48}
        width="100%"
        height={600}
        openByDefault={false}
        initialData={sortedData}
        searchTerm={filter}
        searchMatch={searchMatch}
        onSelect={handleSelect}
      >
        {ComplaintRow}
      </Tree>
    </div>
  );
};